import React, { useEffect, useState } from 'react';

import { Card } from '@components/common';
import { SlideOut } from '@components/SlideOut';
import { StudyCheckoutInline } from '@components/StudyCheckout';
import { useStudyFunder } from '@components/Wallet';

import { PublishStudyButtonConditional } from '../../StudyDraft/components/PublishStudyButtonConditional';
import { useStudyPublishValidator } from '../../StudyDraft/hooks/useStudyPublishValidator';
import { RequestFundInfo, WalletInfo } from '../../StudyDraft/pages/Publish/components';
import { GreenCheckRowItem } from '../../StudyDraft/pages/Publish/components/GreenCheckRowItem/GreenCheckRowItem';
import { DraftStudyContext } from '../../StudyDraft/StudyDraft';

interface ReviewStepSlideoutProps {
  study: Study;
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
}

export const ReviewStepSlideout: React.FC<ReviewStepSlideoutProps> = ({ study, isOpen, onClose, refetch }) => {
  const [saving, setSaving] = useState(false);
  const [prefundLimit, setPrefundLimit] = useState(study.participant_limit || 0);
  const funder = useStudyFunder({ study });

  const validator = useStudyPublishValidator({ study, flow: 'active' });

  useEffect(() => {
    setPrefundLimit(study.maximum_slots);
  }, [study.maximum_slots]);

  useEffect(() => {
    funder.prepare({ participant_limit: prefundLimit });
  }, [study, prefundLimit]);

  const contextValue = {
    funder,
    prefundLimit,
    setPrefundLimit
  };

  let fundInfo: React.ReactElement | null = null;
  switch (funder.mode) {
    case 'request':
      fundInfo = <RequestFundInfo amount={funder.displayTotal} study={study} />;
      break;
    case 'fund':
      fundInfo = <WalletInfo study={study} funder={funder} />;
      break;
  }

  const shouldShowPublishButton = study.state === 'draft';

  const publishButton = shouldShowPublishButton && (
    <PublishStudyButtonConditional
      study={study}
      saving={saving}
      setSaving={setSaving}
      hasErrors={!validator.canPublish}
    />
  );

  if (!isOpen) return null;

  return (
    <DraftStudyContext.Provider value={contextValue}>
      <SlideOut
        isOpen={isOpen}
        onClose={(reason) => {
          if (reason !== 'clickOutside') {
            onClose();
          }
        }}
        width='800px'
      >
        <div className='flex h-full flex-col'>
          <div className='border-b border-gray-200 bg-white px-6 py-4'>
            <h2 className='text-lg font-semibold text-gray-900'>Review and create</h2>
          </div>
          <div className='flex-1 overflow-auto p-6'>
            <Card className='mx-auto max-w-3xl px-12'>
              {validator.items.map((item, i) => (
                <GreenCheckRowItem
                  study={study}
                  key={i}
                  noBorder={i === validator.items.length - 1 && !fundInfo}
                  {...item}
                />
              ))}
              {fundInfo}
              {study.state !== 'draft' && (
                <div className='mt-6 space-x-4 py-6 text-center'>
                  <span className='h500'>Study is in {study.state} state.</span>
                </div>
              )}
            </Card>

            <StudyCheckoutInline study={study} />
          </div>

          {shouldShowPublishButton && (
            <div className='border-t border-gray-200 bg-gray-50 p-6'>
              <div className='flex justify-end'>{publishButton}</div>
            </div>
          )}
        </div>
      </SlideOut>
    </DraftStudyContext.Provider>
  );
};
